<template>
  <div>
    <div v-if="isContentReady" class="view-container">
      <!--
        Nested child route (/email-accounts/pending-accounts) renders
        here. EmailAccounts stays mounted as the parent so its data
        (email list, plan details) remains warm while the child shows.
        The sidebar's Email-accounts entry stays highlighted because
        its config includes the child path explicitly.
      -->
      <router-view v-if="isChildRouteActive" />
      <!--
        Create email takes over the content area: a full-page modal card in
        iframe view (consistent with sibling modals like CatchAllEmail,
        ResetPassword) and an inline page-style render in standalone CP.
        The listing and header are hidden while Create is showing.
      -->
      <create-email
        v-else-if="showCreateEmail"
        @close="onCreateEmailClose"
        @nudge-create-blocked="onNudgeCreateBlocked"
        @delete-and-create-consumed="onDeleteAndCreateConsumed"
        :hasAdmin="emailToDeleteDetails.accountId ? false : hasAdmin"
        :view="view"
        :is-first-creation="isFirstCreationMode"
        :emailToDeleteDetails="emailToDeleteDetails"
      />

      <template v-else>
        <b-row>
          <b-col>
            <h4 class="emailHeader">
              {{ $t("emailAccounts.heading") }}
            </h4>
          </b-col>
          <b-col cols="8" v-if="hasEmails">
            <div class="search-container email-accounts-search">
              <span class="search-wrapper">
                <input
                  type="text"
                  class="input"
                  v-model="search"
                  v-bind:placeholder="$t('placeholders.searchUser')"
                  autocomplete="new-password"
                />
                <span class="clear-search" v-show="search.length > 0" @click="search = ''"></span>
              </span>
              <button
                class="button"
                @click="onCreateEmailAccountClick"
                :class="{ 'show-spinner': showSpinner }"
              >
                {{ $t("emailAccounts.createAccount") }}
              </button>
            </div>
          </b-col>
        </b-row>

        <!--
          Pending accounts banner. Driven by the
          `pendingBannerCopy` getter — admin-added + team-requested items;
          null when nothing is pending. Clicking "Review pending details"
          opens the dedicated Pending screen.
        -->
        <nudge-banner
          v-if="pendingBannerCopy"
          variant="promo"
          action-style="link"
          :icon-src="infoIcon"
          :action-label="$t('emailAccounts.pending.banner.reviewCta')"
          @action="goToPendingAccounts"
          class="nudge-banner"
        >
          {{ $t(pendingBannerCopy.key, pendingBannerCopy.params) }}
        </nudge-banner>

        <div v-show="!hasEmails" class="card-container no-email-state">
          <div>
            <img :src="'empty-state' | themeImage" alt="" />
          </div>
          <p>{{ $t("emailAccounts.createAccountMsg") }}</p>
          <button class="button" @click="onCreateEmailAccountClick('list')">
            {{ $t("emailAccounts.createAccount") }}
          </button>
        </div>

        <div v-show="hasEmails" class="card-container">
          <table class="table email-list">
            <thead>
              <tr>
                <th v-bind:key="index" v-for="(field, index) in fields" scope="col">
                  {{ field }}
                </th>
              </tr>
            </thead>
            <tbody>
              <email-list
                :catchAllEmail="catchAllEmail"
                v-for="account in filteredList"
                :key="filteredList[account]"
                :user="account"
                :activeUserId="activeUserId"
                :setActiveUserId="setActiveUserId"
                :on-remove="onRemove"
                :view="view"
                :isLastAccount="isLastAccount"
                :isLastAdminAccount="isLastAdminAccount"
                :on-confirm-create-and-delete="onConfirmCreateAndDelete"
              ></email-list>
              <tr v-if="filteredList.length === 0" class="no-emails-found">
                <td colspan="3">
                  {{ $t("emailAccounts.noAccountFound") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    <Loader v-else />
    <PaymentGatewayModal
      v-if="modalConfig.showModal"
      :config="modalConfig"
      :view="view"
    ></PaymentGatewayModal>
    <alt-user-email-modal v-if="showAltUserEmailModal" @close="closeAltUserEmailModal" />
  </div>
</template>

<script>
import { BCol, BRow } from "bootstrap-vue";
import { mapGetters } from "vuex";
import expiredImg from "../../assets/expired.svg";
import upgradeFailImg from "../../assets/upgrade-failure.png";
import upgradeImage from "../../assets/upgrade-success.png";
import infoIcon from "../../assets/info-i.svg";
import * as helper from "../../helpers";
import { clearLaunchEmailCreation } from "../../helpers/emailAccount.helper";
import viewTypes from "../../views";
import AltUserEmailModal from "../Utility/AltUserEmailModal";
import Loader from "../Utility/Loader";
import PaymentGatewayModal from "../Utility/PaymentGatewayModals.vue";
import { partnerHandler, partnerMatcher } from "@/helpers/partner";
import CreateEmail from "./CreateEmail";
import EmailList from "./EmailList";
import NudgeBanner from "@/components/Banner/NudgeBanner.vue";
import pendingAccountsService from "@/services/pending-accounts/pending-accounts.service";
import Logger from "@/helpers/logger";
import { trackPendingBannerShown, trackPendingBannerClicked } from "@/telemetry/mailboxNudgeEvents";
import { measurePagePerformance } from "@/analytics/performance/pageTimings";
import emailService from "@/services/email/email.service";
import billingService from "@/services/billing/billing.service";
import ProductRouteGuardMixin from "@/mixins/ProductRouteGuardMixin";
import { getPendingBannerCopy } from "@/helpers/mailboxNudge";

export default {
  name: "EmailAccounts",
  components: {
    EmailList,
    CreateEmail,
    PaymentGatewayModal,
    AltUserEmailModal,
    Loader,
    BRow,
    BCol,
    NudgeBanner,
  },
  props: {
    view: Object,
    partnerId: Number,
  },
  mixins: [ProductRouteGuardMixin],
  data() {
    const hasLaunchEmailCreationFlag = !!smartStorage.getItem("launchEmailCreation");
    const hasShowAltUserEmailFlag = smartStorage.getItem("showAltUserEmail");
    const showCreateEmail =
      hasLaunchEmailCreationFlag && !hasShowAltUserEmailFlag && this.shouldOpenCreateEmailModal();
    return {
      showCreateEmail,
      // `showCreateEmail` above comes from the `launchEmailCreation` sentinel,
      // decided before the account list is known — so the create gate couldn't
      // run yet. One-shot: `onSuccess` re-checks it on the first list load and
      // clears this. It must NOT apply to later list loads, which include the
      // refetch after a successful create (CreateEmail is then showing its
      // success screen, and closing that to show the used-license screen would
      // be wrong — the user didn't ask to create again).
      gateCreateOnFirstListLoad: showCreateEmail,
      // Tracks whether the currently-open Create email modal is the
      // first-mailbox landing experience. Set by
      // maybeAutoOpenFirstMailboxCreate; cleared on close.
      isFirstCreationMode: false,
      // Set when the user confirms deleting their last mailbox and creating a
      // replacement in one step (Neo last-account flow). Drives the page-level
      // Create form's delete-and-create mode; reset on close.
      emailToDeleteDetails: { accountId: false, emailId: "" },
      showSpinner: false,
      catchAllEmail: "",
      search: "",
      accounts: [],
      hasAdmin: false,
      hasEmails: false,
      pageLoaded: false,
      addons: [],
      knownAddons: ["storage_addon", "titan_max"],
      modalCallback: () => {},
      isSent: false,
      modalConfig: {},
      showAltUserEmailModal: hasShowAltUserEmailFlag,
      activeUserId: -1,
      // Analytics: pending-banner impression fired (once per mount).
      pendingBannerShownTracked: false,
      theme: null,
      openPaymentURLInDiffTab:
        viewTypes[document.documentElement.getAttribute("view")]["openPaymentURLInDiffTab"],
    };
  },
  computed: {
    ...mapGetters({
      plans: "mail/plans",
      isFreePlan: "mail/isFreePlan",
      isTrialPlan: "mail/isTrialPlan",
      currentPlan: "currentPlanDetails",
      currentPlanMailDetails: "mail/currentProductDetails",
      isBundleExpired: "bundle/isExpired",
      availableLicenses: "mail/availableLicenses",
      adminPendingItems: "mail/mailboxNudge/adminPendingUsernames",
      teamPendingEmails: "mail/mailboxNudge/teamPendingEmails",
      domainName: "domain/domainName",
      isCurrentPlanFetched: "isCurrentPlanFetched",
      canBuyMoreAccounts: "mail/canBuyMoreAccounts",
      partnerInfo: "partnerInfo",
    }),
    /**
     * Gate for the content area (loader otherwise). The email list alone isn't
     * enough: for a user with no mailboxes the first-mailbox auto-open depends
     * on the plan/licenses, which can arrive after the list. Holding the loader
     * until the plan is fetched avoids flashing the empty listing for a moment
     * before Create auto-opens (same race as the availableLicenses watcher).
     * Users who already have mailboxes don't wait on the plan.
     */
    isContentReady() {
      if (!this.pageLoaded) return false;
      if (!this.hasEmails && !this.isCurrentPlanFetched) return false;
      return true;
    },
    /**
     * Pending-accounts banner copy — derived via the pure helper from raw store
     * state. Returns `{ key, params }` for `$t` rendering, or null
     * when there's nothing pending.
     */
    pendingBannerCopy() {
      return getPendingBannerCopy({
        adminItems: this.adminPendingItems || [],
        teamItems: this.teamPendingEmails || [],
        domain: this.domainName || "",
      });
    },
    /**
     * Static — exposed as a computed so the template can use a literal
     * src binding without dropping the webpack-import. Kept here (vs.
     * inlining via `require()` in the template) so the asset stays in
     * the build graph and gets the proper hashed URL.
     */
    infoIcon() {
      return infoIcon;
    },
    /**
     * True when a child route under /email-accounts is currently
     * matched. Drives the parent's forked render: child takes over via
     * <router-view>, else the existing listing / create-email /
     * nudge-banner block renders. Add new child route names here.
     */
    isChildRouteActive() {
      return ["pending-accounts"].includes(this.$route.name);
    },
    fields() {
      return [
        this.$t("emailAccounts.createEmail.emailLabel"),
        this.$t("emailAccounts.status"),
        this.$t("emailAccounts.quota"),
      ];
    },
    isPaid() {
      return this.currentPlanMailDetails.isPaid;
    },
    isProductExpiredBeforeBundle() {
      return helper.isProductExpiredBeforeBundle(this.currentPlanMailDetails, this.currentPlan);
    },
    filteredList() {
      return this.accounts.filter(account =>
        account.email.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    emailAccountCount() {
      return (
        this.currentPlanMailDetails.noOfAccounts?.limit ||
        this.currentPlanMailDetails.noOfAccounts?.purchased
      );
    },
    allowAccountLimit: function () {
      return this.emailAccountCount || "100000";
    },
    conjugatePlanOfTriaPlan() {
      //e.g Business Starter Trial => Business Starter
      return this.plans.find(
        plan => plan.id == this.currentPlanMailDetails.planDetails?.paidPlanId
      );
    },
    isAccountLimitReached() {
      const { limit, active } = this.currentPlanMailDetails.noOfAccounts;
      return limit == active;
    },
    isLastAccount() {
      return this.accounts?.length <= 1;
    },
    isLastAdminAccount() {
      return this.accounts.filter(account => !!account?.isAdmin)?.length <= 1;
    },
  },
  methods: {
    setActiveUserId(id) {
      this.activeUserId = id;
    },
    closeAltUserEmailModal() {
      this.showAltUserEmailModal = false;
      if (!!smartStorage.getItem("launchEmailCreation")) {
        this.openCreateEmailModal();
      }
    },
    /**
     * Why a create can't proceed right now, or null when it can. Single gate
     * for every create entry point — the listing's own buttons, the
     * `launchEmailCreation` sentinel other surfaces set, and the nudge
     * surfaces, whose Create CTA is what's shown when there are no licenses
     * left and no buy path available (see `shouldOfferBuy`). Callers therefore
     * pass nothing: this component already holds the plan and account data the
     * decision needs.
     *
     * Before the plan and email list load this reads "not blocked"
     * (`allowAccountLimit` falls back to a huge number), same as today's
     * click-driven gate — so it never turns a slow fetch into a dead end.
     */
    blockedCreateModalType() {
      if (this.isTrialPlan && this.isAccountLimitReached) return "cantAddAccountForTrialPlan";
      if (this.isBundleExpired && !this.isFreePlan) return "planExpired";
      if (this.isProductExpiredBeforeBundle) return "planExpired";
      if (this.allowAccountLimit > this.accounts.length) return null;
      return "cantAddAccountForNonTrialPlan";
    },
    showBlockedCreateModal(type) {
      this.updateModalConfig(
        type === "cantAddAccountForNonTrialPlan"
          ? { type, noOfAccounts: this.allowAccountLimit }
          : { type }
      );
    },
    onCreateEmailAccountClick(type = "top-bar") {
      // Partner owns email-account creation: hand off to their surface instead
      // of opening the in-app Create flow.
      if (this.maybeHandOffCreateToPartner()) return;
      // Analytics source for the Create screen this click may open
      // (consumed read-once by CreateEmail).
      smartStorage.setItem("launchEmailCreationSource", "email_listing_screen_button");
      this.showSpinner = true;
      this.$emit("fetchCurrentPlan");
      this.showSpinner = false;
      const blockedType = this.blockedCreateModalType();
      if (blockedType) {
        this.showBlockedCreateModal(blockedType);
      } else {
        this.createEmailAccount(() => {});
      }
      const actionType = blockedType
        ? blockedType === "planExpired"
          ? "planExpired"
          : "cantAddAccount"
        : "";
      let eventObj = {
        eventName: "mail_account_creation_initiated",
        partner_id: this.partnerId,
        source_hook: type === "list" ? "empty_screen_button" : "email_listing_screen_button",
      };
      if (actionType) eventObj["info"] = actionType;
      this.medusaEvents.send(
        this.medusaEvents.createEventObj(eventObj, helper.getDomainName(), "flock_order")
      );
    },
    getEmailList() {
      if (!this.isSent) {
        this.isSent = true;
        this.fetchAddons()
          .then(data => {
            // success
            this.addons = this.updatedRecords(
              data.addons.filter(a => this.knownAddons.indexOf(a.type) !== -1)
            );
            return emailService.getEmailList();
          })
          .then(this.onSuccess)
          .catch(this.onError);
      }
    },
    getCatchAllEmail() {
      this.$http
        .get(`panel/domain/catchall/get`)
        .then(({ email }) => {
          this.catchAllEmail = email;
        })
        .catch(err => console.error(err));
    },
    getAddonDetails(addon, key) {
      if (!addon) {
        return this.$t("billingAndUpgrade.orderSummarySection.addon"); // empty placehoder, incase of get all addons return empty but calc charge returns some addon
      }

      switch (addon.type) {
        case "storage_addon":
          const { respFeature, respAttr, attrValue } = helper.getFeatureDetails.call(
            this,
            addon.features,
            "storage"
          );
          return this.$t(
            `billingAndUpgrade.addons.features.${respFeature.type}.${respAttr}.${key}`,
            [attrValue]
          );

        default:
          return "";
      }
    },
    updatedRecords(records) {
      return records
        .map(r => {
          r.features = r.features.map(f => {
            if (f.type === "storage") {
              f.attrs = { ...f.attrs }; // create new copy of attr and then update, do not update original data
              const quota = parseInt(f.attrs.quota_in_mb, 10);
              const convertedQuota = (
                f.attrs.quota_in_gb || helper.toGB(f.attrs.quota_in_mb)
              ).toString();
              if (helper.isSizeInGB(quota)) {
                f.attrs.quota_in_gb = convertedQuota;
                delete f.attrs.quota_in_mb;
              } else {
                f.attrs.quota_in_mb = convertedQuota;
              }
            }

            return f;
          });

          if (!r.pricing) {
            return {
              ...r,
              pricing: { monthly: 0, yearly: 0 },
            };
          }

          return r;
        })
        .sort((a, b) => a.zIndex > b.zIndex);
    },
    fetchAddons() {
      return billingService.getAllAddons();
    },
    onSuccess(data) {
      if (data?.accounts?.length > 0) {
        data.accounts = data.accounts.map(ac => {
          // mutation is necessary
          ac.addons = ac.addons || [];
          ac.addonHeadings = ac.addonHeadings || [];
          const cAddon = this.currentPlan.addons?.find(ad => ad.attachedTo === ac.id);
          if (cAddon) {
            ac.addons.push(cAddon.addonId);
            ac.addonHeadings.push(
              this.getAddonDetails(
                this.addons.find(ad => ad.id === cAddon.addonId),
                "title"
              )
            );
          }

          return ac;
        });

        this.accounts = data.accounts;
        this.hasEmails = true;
        this.hasAdmin = data.accounts.some(a => a.isAdmin);
      } else {
        this.hasEmails = false;
        this.hasAdmin = false;
      }
      this.isSent = false;
      this.pageLoaded = true;
      this.$store.dispatch("currentDomainMailboxDetails", data?.accounts || []);
      // Partner owns email-account creation: a launchEmailCreation-driven
      // auto-open (buy-mailbox success, nudges, provisioning) hands off to the
      // partner's surface instead of the in-app form. Runs here — after data()
      // set `showCreateEmail` from the sentinel and the account list is known.
      // The first-mailbox auto-open below bails for partners, so this is the
      // only on-load handoff.
      if (this.showCreateEmail && this.maybeHandOffCreateToPartner()) {
        this.showCreateEmail = false;
        this.gateCreateOnFirstListLoad = false;
      }
      // Run the create gate for a sentinel-opened Create now that the account
      // list is known (see `gateCreateOnFirstListLoad`). The Create screen
      // renders behind `isContentReady`, i.e. not before this point, so this
      // swaps in the used-license screen without any flash of the form.
      if (this.gateCreateOnFirstListLoad) {
        this.gateCreateOnFirstListLoad = false;
        const blockedType = this.showCreateEmail && this.blockedCreateModalType();
        if (blockedType) {
          this.showCreateEmail = false;
          this.showBlockedCreateModal(blockedType);
        }
      }
      this.maybeAutoOpenFirstMailboxCreate();
    },
    onError(response) {
      console.error(response);
      if (response && response.data && response.data.desc) {
        this.showToast(response.data.desc);
      }
      this.isSent = false;
      this.pageLoaded = true;
    },
    onRemove(user) {
      this.accounts = this.accounts.filter(account => account !== user);
    },
    updateModalConfig(data) {
      const isPaid = this.isPaid;
      switch (data.type) {
        case "planUpgraded":
          this.modalConfig = {
            className: this.view.showModalAsPage && "modalPage",
            imgUrl: upgradeImage,
            modalHeading: this.$t("planUpgradedModal.heading"),
            subHeading: this.$t("planUpgradedModal.description", [
              data.noOfAccounts,
              data.planType,
            ]),
            btnTxt: this.$t("planUpgradedModal.button"),
            btnCB: this.closeModal,
            showModal: true,
          };
          break;
        case "error":
          this.modalConfig = {
            className: this.view.showModalAsPage && "modalPage",
            imgUrl: upgradeFailImg,
            modalHeading: this.$t("errorModal.heading"),
            subHeading: this.$t("errorModal.description"),
            btnTxt: "",
            showModal: true,
          };
          break;
        case "planExpired":
          this.modalConfig = {
            imgUrl: expiredImg,
            className: this.view.showModalAsPage ? "error-modal modalPage" : "error-modal",
            imgHeight: 87,
            modalHeading: this.$t("planExpiredModal.heading"),
            subHeading: this.$t("planExpiredModal.createEmail"),
            btnTxt: this.$t("planExpiredModal.ok"),
            btnCB: this.closeModal,
            showModal: true,
          };
          break;
        case "cantAddAccountForTrialPlan":
          this.modalConfig = {
            themeImg: "used-license",
            themeImgExt: "png",
            imgWidth: 191,
            className: this.view.showModalAsPage && "modalPage",
            action: "buy-email-account",
            imgHeight: 200,
            modalHeading: this.$t("trialPlanAddAccount.heading"),
            subHeading: this.$t("trialPlanAddAccount.desc", [
              this.conjugatePlanOfTriaPlan.displayName,
              this.currentPlanMailDetails?.noOfAccounts?.limit,
            ]),
            btnTxt:
              this.canBuyMoreAccounts &&
              smartStorage.getItem("paymentEnabled") &&
              smartStorage.getItem("paymentEnabled").toString() === "true"
                ? isPaid
                  ? this.$t("modalThree.buyButton")
                  : this.$t("modalThree.upgradeButton")
                : "",
            btnCB: this.buyMoreEmailAccounts,
            showModal: true,
          };
          break;
        case "cantAddAccountForNonTrialPlan":
          this.modalConfig = {
            themeImg: "used-license",
            themeImgExt: "png",
            imgWidth: 191,
            className: this.view.showModalAsPage && "modalPage",
            action: "buy-email-account",
            imgHeight: 200,
            modalHeading: this.$t(`modalThree.${isPaid ? "heading" : "headingThree"}`),
            subHeading: isPaid
              ? this.$t("modalThree.subHeading", [
                  (smartStorage.getItem("plan") || "").toUpperCase(),
                ])
              : this.$t("modalThree.subHeadingThree", [
                  (smartStorage.getItem("plan") || "").toUpperCase(),
                  data.noOfAccounts,
                ]),
            btnTxt:
              this.canBuyMoreAccounts &&
              smartStorage.getItem("paymentEnabled") &&
              smartStorage.getItem("paymentEnabled").toString() === "true"
                ? isPaid
                  ? this.$t("modalThree.buyButton")
                  : this.$t("modalThree.upgradeButton")
                : "",
            btnCB: this.buyMoreEmailAccounts,
            showModal: true,
          };
          break;
        case "purchasedAccounts":
          this.modalConfig = {
            className: this.view.showModalAsPage && "modalPage",
            imgUrl: upgradeImage,
            modalHeading: this.$t("purchasedAccountsModal.heading", [data.noOfAccounts]),
            subHeading: this.$t("purchasedAccountsModal.description"),
            btnTxt: this.$t("purchasedAccountsModal.button"),
            btnCB: this.createEmailAccount,
            showModal: true,
          };
          break;
        default:
          return false;
      }
    },
    closeModal(callback) {
      callback();
    },
    buyMoreEmailAccounts(callback) {
      this.modalCallback = callback;
      return billingService
        .checkPaymentType(this.isPaid ? "buyMoreAccounts" : "upgradePlan")
        .then(this.trasactionRedirectInitiate);
    },
    trasactionRedirectInitiate(data) {
      var eventObj = {
        eventName: "upgrade",
        source: helper.getAppSource(),
      };
      const callback = this.modalCallback;
      callback();
      if (
        helper.isAppLoadedInsideIframe() &&
        this.$store.getters.flags.disableIFramePaymentRedirection
      ) {
        helper.handleRedirectionOnPartnerSide(data.jwt);
        return;
      }
      const cpURL = encodeURIComponent(`${window.location.origin}/postPayment`);
      const redirectURL = `${data.makePaymentUrl}?jwt=${data.jwt}&redirect_url=${cpURL}`;
      this.medusaEvents
        .send(this.medusaEvents.createEventObj(eventObj, helper.getDomainName(), "flock_order"))
        .then(() => {
          if (data.makePaymentUrl == "inAppPayment") {
            this.$router.push({
              path: this.isPaid ? "/buy-email-account" : "/billing-and-upgrade",
              query: {
                redirectionSource: "mailbox_limit_popup",
              },
            });
            return false;
          }

          smartStorage.hostRedirectURL = redirectURL;
          helper.hardRedirect(
            redirectURL,
            false,
            this.openPaymentURLInDiffTab && data.makePaymentUrl !== "inAppPayment"
          ); // skip true, external URL
        })
        .catch(() => {
          smartStorage.hostRedirectURL = redirectURL;
          helper.hardRedirect(redirectURL); // skip true, external URL
        });
    },
    createEmailAccount(callback) {
      if (callback) {
        callback();
      }
      this.openCreateEmailModal();
    },
    /**
     * Partner-provided URL that owns email-account creation, or "" when the
     * partner doesn't.
     */
    partnerCreateEmailUrl() {
      return this.partnerInfo?.urls?.createEmailAccount || "";
    },
    /** Open the partner's own create-email surface in a new tab. */
    openPartnerCreateEmail() {
      // noopener,noreferrer: the URL is partner-configured backend data, not a
      // fixed first-party origin, so the opened page must not keep a
      // `window.opener` handle back to this logged-in admin dashboard
      // (reverse-tabnabbing).
      window.open(this.partnerCreateEmailUrl(), "_blank", "noopener,noreferrer");
      // Consume the create-intent sentinel, exactly as CreateEmail does when it
      // opens. We redirect before CreateEmail ever mounts, so without this the
      // flag would persist in storage (smartStorage is local/sessionStorage
      // backed) and re-fire the redirect on every plain /email-accounts refresh.
      // Cleared here, the handoff only happens on a fresh create action or a
      // URL/route param that re-sets the sentinel for that navigation.
      clearLaunchEmailCreation();
    },
    /**
     * Single guard for every create entry point: when a partner owns
     * email-account creation, open their surface instead of the in-app Create
     * form and return true so the caller bails. Used by explicit clicks
     * (onCreateEmailAccountClick, onConfirmCreateAndDelete) and the
     * programmatic paths (the launchEmailCreation sentinel in onSuccess,
     * openCreateEmailModal). Returns false when no partner URL is configured.
     *
     * No dedupe is needed: the first-mailbox auto-open bails early for partners
     * (see maybeAutoOpenFirstMailboxCreate), so a single load hands off at most
     * once, and every explicit click should open a fresh tab.
     */
    maybeHandOffCreateToPartner() {
      if (!this.partnerCreateEmailUrl()) return false;
      this.openPartnerCreateEmail();
      return true;
    },
    shouldOpenCreateEmailModal() {
      // as we only domain partner will take care of email creation
      if (partnerMatcher.isOnyDomains(this.$store.getters.partnerId)) {
        partnerHandler.onlyDomainHandleEmailCreation();
        return false;
      }

      return true;
    },
    openCreateEmailModal() {
      // Partner owns email-account creation — hand off to their surface instead
      // of opening the in-app form (covers the first-mailbox auto-open, the
      // alt-user-email follow-up, and the return-from-pending re-open).
      if (this.maybeHandOffCreateToPartner()) return false;
      if (!this.shouldOpenCreateEmailModal()) return false;
      // Surfaces that route here to create (pending screen, home nudge,
      // post-payment) don't check whether a create is possible — this gate
      // does, and shows the used-license screen instead when it isn't.
      const blockedType = this.blockedCreateModalType();
      if (blockedType) {
        this.showCreateEmail = false;
        this.showBlockedCreateModal(blockedType);
        return false;
      }
      this.showCreateEmail = true;
    },
    /**
     * Standalone CP ("licenses available, no mailboxes created"):
     * the Email accounts tab opens with the Create email account screen
     * visible by default, and re-shows it whenever the user navigates away
     * and back. Re-show happens implicitly because `created()` triggers a
     * fresh `getEmailList()` → `onSuccess` on each remount.
     */
    maybeAutoOpenFirstMailboxCreate() {
      if (this.hasEmails) return;
      if (this.availableLicenses <= 0) return;
      // alt-user-email modal takes precedence if it's still open.
      if (this.showAltUserEmailModal) return;
      // Partner owns creation: don't hand off to their surface on load. This is
      // an auto-open (no user click), so redirecting here would bounce a
      // mailbox-less user straight off the page — e.g. before they can review
      // pending requests. The handoff happens only on an explicit create action
      // (the listing's Create button, the Create-and-delete confirm, or the
      // pending page's Create account CTA, which routes through the sentinel).
      if (this.partnerCreateEmailUrl()) return;
      this.isFirstCreationMode = true;
      this.openCreateEmailModal();
    },
    /**
     * The success-screen nudge asked for another create with no license left
     * (its Create CTA when buying isn't available). Same gate as every other
     * create entry point — in practice the used-license screen.
     */
    onNudgeCreateBlocked() {
      this.showCreateEmail = false;
      this.isFirstCreationMode = false;
      const blockedType = this.blockedCreateModalType();
      if (blockedType) this.showBlockedCreateModal(blockedType);
    },
    onCreateEmailClose() {
      this.showCreateEmail = false;
      this.gateCreateOnFirstListLoad = false;
      this.isFirstCreationMode = false;
      this.emailToDeleteDetails = { accountId: false, emailId: "" };
    },

    /**
     * Neo "delete last mailbox + create a replacement" flow. Invoked by the
     * EmailList row's last-account-delete dialog. Opens the page-level Create
     * form in delete-and-create mode so it takes over the content area like a
     * normal create — rather than rendering inside the table row, which broke
     * the layout (the form's host element was a `<tr>`).
     */
    onConfirmCreateAndDelete({ accountId, emailId }) {
      // Partner owns email-account creation: hand off to their surface instead
      // of opening the in-app delete-and-create form.
      if (this.maybeHandOffCreateToPartner()) return;
      this.emailToDeleteDetails = { accountId, emailId };
      this.showCreateEmail = true;
    },
    /**
     * The delete-and-create pairing has been fulfilled by CreateEmail. Clear
     * it immediately so a nudge-driven follow-up create in the same modal
     * session falls through to a plain create instead of re-issuing
     * deleteAndCreate against the now-deleted oldAccountId.
     */
    onDeleteAndCreateConsumed() {
      this.emailToDeleteDetails = { accountId: false, emailId: "" };
    },
    // App.vue toggles its layout chrome (breadcrumb, powered-by footer, help
    // icon) on this `pageLoaded` signal. The iframe create-email page renders
    // its own breadcrumb in normal flow, so suppress App's chrome while it is
    // showing to avoid a duplicate breadcrumb/footer.
    emitAppChromeState() {
      const createPageActive = this.showCreateEmail && this.view.showModalAsPage;
      this.$emit("pageLoaded", this.pageLoaded && !createPageActive);
    },
    goToPendingAccounts() {
      trackPendingBannerClicked();
      // Passed verbatim as the pending page's viewed-event source_hook.
      this.$router.push({
        name: "pending-accounts",
        query: { sourceHook: "email accounts page pending banner" },
      });
    },
  },
  watch: {
    /**
     * Analytics: pending banner impression — once per mount, when the
     * copy first resolves to something visible.
     */
    pendingBannerCopy: {
      immediate: true,
      handler(copy) {
        if (copy && !this.pendingBannerShownTracked) {
          this.pendingBannerShownTracked = true;
          trackPendingBannerShown();
        }
      },
    },
    pageLoaded: {
      immediate: true,
      handler() {
        this.emitAppChromeState();
      },
    },
    // The iframe create-email page renders in normal flow (not a fixed
    // overlay) and draws its own breadcrumb, so re-evaluate App.vue's chrome
    // whenever it opens/closes to avoid a duplicate breadcrumb/footer.
    showCreateEmail() {
      this.emitAppChromeState();
    },
    // On a hard refresh the email list can resolve before plan/license data,
    // so the first-mailbox auto-open in `onSuccess` bails (availableLicenses
    // reads 0 until the plan loads) and the empty listing shows instead of the
    // Create screen (MWDC-6303). Re-evaluate once licenses arrive — guarded by
    // `pageLoaded` so we only act after the email list is known, and
    // `maybeAutoOpenFirstMailboxCreate` itself no-ops once mailboxes exist.
    availableLicenses(licenses) {
      if (licenses > 0 && this.pageLoaded) {
        this.maybeAutoOpenFirstMailboxCreate();
      }
    },
  },
  /**
   * The pending-accounts screen is a nested child route, so EmailAccounts
   * stays mounted as its parent — created()/data() (where `launchEmailCreation`
   * is normally consumed to auto-open Create) don't re-run when the user
   * routes back here. `beforeRouteUpdate` is the idiomatic hook for a reused
   * component whose route changed: PendingAccounts' Create sets the flag and
   * routes here, and we consume it as the parent↔child route updates.
   */
  beforeRouteUpdate(to, from, next) {
    const returnedFromPending = from.name === "pending-accounts" && to.name === "EmailAccounts";
    if (returnedFromPending && !!smartStorage.getItem("launchEmailCreation")) {
      this.openCreateEmailModal();
    }
    next();
  },
  mounted() {
    measurePagePerformance();
  },
  beforeDestroy() {
    // Inline Create email has no Cancel; the only way to "close" it is to
    // leave the page. Clear the launchEmailCreation flag here so revisiting
    // doesn't immediately re-open Create from a stale flag. The first-
    // mailbox auto-open still re-fires correctly on revisit — it's driven
    // by availableLicenses + hasEmails (see maybeAutoOpenFirstMailboxCreate),
    // not by this storage key.
    clearLaunchEmailCreation();
  },
  created() {
    this.theme = document.documentElement.getAttribute("theme");
    if (this.accounts.length === 0) {
      this.getEmailList();
      this.getCatchAllEmail();
    }
    // Fire-and-forget; optional data, but log failures so they don't
    // become silent unhandled rejections.
    pendingAccountsService
      .fetchPendingAccounts()
      .catch(e => Logger.warn("EmailAccounts: pendingAccounts fetch failed", e));
    if (this.$route.params.modalData) {
      let modalData = this.$route.params.modalData;
      this.updateModalConfig(modalData);
    }
    if (!smartStorage.getItem("firstRender")) {
      smartStorage.firstRender = true;
      var eventObj = {
        eventName: "cloud_control_panel_login",
        source: document.referrer.includes("flockmail") // TODO: Need to revisit this, should we consume source as query param?
          ? "login_button"
          : "cpanel_home",
      };
      this.medusaEvents.send(
        this.medusaEvents.createEventObj(eventObj, helper.getDomainName(), "flock_order")
      );
    }
  },
};
</script>

<style lang="scss">
.emailHeader {
  font-size: var(--h4Size);
}
.email-list {
  margin-bottom: 0;

  thead {
    tr {
      &:hover {
        background: transparent;
      }

      th {
        border-bottom: 1px solid var(--lineSepaCol);
        border-top: 0;
        color: var(--tableHeaderCol);

        &:first-of-type {
          width: 35%;
        }

        &:nth-of-type(2) {
          width: 20%;
        }
      }
    }
  }

  thead {
    tr {
      height: auto;

      th {
        padding-top: 0;
        padding-bottom: 20px;
      }
      &:hover {
        color: var(--primaryTextCol);
      }
    }
  }

  tr {
    height: 65px;
    position: relative;
    td {
      vertical-align: middle;
      line-height: 20px;
      border-bottom: 1px solid var(--lineSepaCol);
      position: relative;
      &:first-child {
        width: 300px;
        word-break: break-all;
      }
      &:last-child {
        text-align: right;
        position: relative;
      }
    }

    &:last-child {
      td {
        border-bottom: 0;
      }
    }

    &.no-emails-found {
      td {
        width: 100%;
        text-align: center;
        padding: 40px 0;
      }
    }

    &:hover,
    &.current-item {
      background: var(--tableDataHoverCol);
      color: var(--genericTextCol);
      div {
        color: var(--genericTextCol);
      }
    }
  }
}
tr.email-accounts-row {
  &:hover {
    background: var(--tableDataHoverCol);
    color: var(--genericTextCol);
    td,
    div,
    p,
    li,
    span {
      color: var(--genericTextCol);
    }
  }
  .modal-overlay {
    .modal-content-wrapper {
      div:not(.error),
      p,
      li,
      span {
        color: var(--primaryTextCol);
      }
    }
  }
}

.nudge-banner {
  padding: 16px 20px;
  margin-top: 20px;
}

.email-accounts-search {
  display: flex;
  justify-content: flex-end;
}
@media (max-width: 1200px) {
  .email-accounts-search {
    flex-direction: row;
    align-items: flex-start;
    .search-wrapper {
      width: 40%;
      margin: 0 0 5px 0;
      margin-right: 20px;
    }
  }
}

.action-button {
  padding: 8px 12px;
  margin-right: 15px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 55px;
  min-width: 140px;
}

.status {
  text-transform: capitalize;

  &:before {
    content: "";
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    margin-right: 8px;
    vertical-align: middle;
    position: relative;
    bottom: 1px;
  }

  &.active:before {
    background: var(--activeCol);
  }

  &.suspended:before {
    background: var(--warningCol);
  }
}

.usage-status,
table th {
  color: var(--primaryTextCol);
}

.usage-available {
  font-size: 12px;
}

.no-email-state {
  text-align: center;
  padding: 122px 24px 64px;

  img {
    margin-bottom: 24px;
  }
}

.overflow-grey {
  cursor: pointer;
  transform: rotate(90deg);
}
.show-spinner {
  @include button-loader();
}
</style>
